import { formatPriceAndCurrency } from '@common/utils'

import {
  MolsonBox,
  MolsonIcon,
  MolsonSpinner,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme'
import { useParams } from 'react-router-dom'

export const BreakdownItem = ({
  archived = false,
  isNonEditableQuote = false,
  loading = false,
  name,
  oldPrice,
  onRemove,
  price,
  removable = false
}: {
  archived?: boolean
  isNonEditableQuote?: boolean
  loading?: boolean
  name: string
  oldPrice?: number
  onRemove?: () => void
  price?: number
  removable?: boolean
}) => {
  const theme = molsonUseTheme()

  const itemSx = () => {
    let color = ''
    let textDecoration = ''
    if (archived) {
      color = theme.palette.primary.error ?? ''
      textDecoration = 'line-through'
    } else if (!archived && removable) {
      color = theme.palette.primary.light
      textDecoration = 'none'
    } else {
      color = theme.palette.secondary[700] ?? ''
    }
    return { color, textDecoration }
  }

  const { specId } = useParams()

  const displayOldPrice = oldPrice !== undefined && !specId
  const displayPrice = price !== undefined

  return (
    <MolsonBox
      alignItems="center"
      display="flex"
      gap={2}
      justifyContent="space-between"
    >
      <MolsonTypography
        sx={{ ...itemSx(), wordBreak: 'break-word', maxWidth: '65%' }}
        variant="body2"
      >
        {name}
      </MolsonTypography>
      <MolsonBox sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        {!archived && (
          <>
            {displayOldPrice && displayPrice && (
              <>
                <MolsonTypography
                  sx={{
                    color: theme.palette.secondary[500],
                    mr: oldPrice === price ? 0.5 : 0,
                    textDecoration: 'line-through'
                  }}
                  variant="body2"
                >
                  {formatPriceAndCurrency(oldPrice)}
                </MolsonTypography>
                {(oldPrice > price || oldPrice < price) && (
                  <MolsonIcon
                    variant={
                      price > oldPrice ? 'ARROW_UPWARD' : 'ARROW_DOWNWARD'
                    }
                    iconColor={price > oldPrice ? 'success' : 'error'}
                    sx={{
                      fontSize: 16
                    }}
                  />
                )}
              </>
            )}
            {displayPrice && (
              <MolsonTypography
                color={theme.palette.secondary[700]}
                variant="body2"
              >
                {formatPriceAndCurrency(price)}
              </MolsonTypography>
            )}
          </>
        )}
        {removable &&
          (loading ? (
            <MolsonSpinner size={18} sx={{ ml: 0.5 }} />
          ) : (
            <MolsonIcon
              variant="HIGHLIGHT_OFF"
              data-testid="breakdown_item_remove_icon"
              onClick={isNonEditableQuote ? undefined : onRemove}
              fontSize="small"
              iconColor="error"
              sx={{
                ml: 0.5,
                opacity: isNonEditableQuote ? '30%' : '100%',
                cursor: isNonEditableQuote ? 'not-allowed' : 'pointer'
              }}
            />
          ))}
      </MolsonBox>
    </MolsonBox>
  )
}
